import React, { useState } from 'react';
import { CardAnalysisReportWithData } from '../../types';
import { downloadMarkdown, downloadPdf } from '../../services/cardAnalysisService';
import ReactMarkdown from 'react-markdown';
import * as XLSX from 'xlsx';

interface ReportDisplayProps {
  report: CardAnalysisReportWithData;
}

const ReportDisplay: React.FC<ReportDisplayProps> = ({ report }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'structured' | 'personal' | 'company'>('structured');

  const handleDownloadMarkdown = () => {
    try {
      const filename = `business-card-report-${report.id}.md`;
      downloadMarkdown(report.report_content, filename);
    } catch (err) {
      setError(`Failed to download markdown: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      setIsDownloading(true);
      setError(null);
      
      const filename = `business-card-report-${report.id}.pdf`;
      await downloadPdf(report.report_content, filename);
    } catch (err) {
      setError(`Failed to download PDF: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadExcel = () => {
    try {
      // 创建工作簿
      const wb = XLSX.utils.book_new();
      
      // 结构化数据工作表
      if (report.report_data?.structured_data) {
        const structuredData = report.report_data.structured_data;
        const structuredRows = [
          ['字段', '值'],
          ['姓名', structuredData.contact_name || ''],
          ['职位', structuredData.position || ''],
          ['公司', structuredData.company_name || ''],
          ['电话', structuredData.phone || ''],
          ['邮箱', structuredData.email || ''],
          ['网站', structuredData.company_website || ''],
          ['地址', structuredData.address || ''],
          ['国家', structuredData.country || ''],
        ];
        const wsStructured = XLSX.utils.aoa_to_sheet(structuredRows);
        XLSX.utils.book_append_sheet(wb, wsStructured, '结构化数据');
      }
      
      // 个人背景报告工作表
      if (report.report_data?.personal_report) {
        const personalRows = [['个人背景报告'], [report.report_data.personal_report]];
        const wsPersonal = XLSX.utils.aoa_to_sheet(personalRows);
        XLSX.utils.book_append_sheet(wb, wsPersonal, '个人背景报告');
      }
      
      // 公司背景报告工作表
      if (report.report_data?.company_report) {
        const companyRows = [['公司背景报告'], [report.report_data.company_report]];
        const wsCompany = XLSX.utils.aoa_to_sheet(companyRows);
        XLSX.utils.book_append_sheet(wb, wsCompany, '公司背景报告');
      }
      
      // 导出文件
      const filename = `business-card-report-${report.id}.xlsx`;
      XLSX.writeFile(wb, filename);
    } catch (err) {
      setError(`导出 Excel 失败: ${err instanceof Error ? err.message : '未知错误'}`);
    }
  };

  const renderStructuredData = () => {
    if (!report.report_data?.structured_data) return null;
    
    const data = report.report_data.structured_data;
    const rows = [
      { label: '姓名', value: data.contact_name },
      { label: '职位', value: data.position },
      { label: '公司', value: data.company_name },
      { label: '电话', value: data.phone },
      { label: '邮箱', value: data.email },
      { label: '网站', value: data.company_website },
      { label: '地址', value: data.address },
      { label: '国家', value: data.country },
    ];

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300">
          <tbody>
            {rows.map((row, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                <td className="px-6 py-4 border-b border-gray-300 font-medium text-gray-900">{row.label}</td>
                <td className="px-6 py-4 border-b border-gray-300 text-gray-700">{row.value || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'structured':
        return renderStructuredData();
      case 'personal':
        return (
          <div className="prose max-w-none">
            <ReactMarkdown>{report.report_data?.personal_report || ''}</ReactMarkdown>
          </div>
        );
      case 'company':
        return (
          <div className="prose max-w-none">
            <ReactMarkdown>{report.report_data?.company_report || ''}</ReactMarkdown>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">名片分析报告</h2>
        <div className="space-x-2">
          <button
            onClick={handleDownloadExcel}
            className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
          >
            导出 Excel
          </button>
          <button
            onClick={handleDownloadMarkdown}
            className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
            disabled={isDownloading}
          >
            下载 MD
          </button>
        </div>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      <div className="mb-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('structured')}
              className={`${
                activeTab === 'structured'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              结构化数据
            </button>
            <button
              onClick={() => setActiveTab('personal')}
              className={`${
                activeTab === 'personal'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              个人背景报告
            </button>
            <button
              onClick={() => setActiveTab('company')}
              className={`${
                activeTab === 'company'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
            >
              公司背景报告
            </button>
          </nav>
        </div>
      </div>
      
      <div className="border rounded-lg p-4 bg-gray-50 overflow-auto max-h-[70vh]">
        {renderContent()}
      </div>
      
      <div className="mt-4 text-sm text-gray-500">
        <p>报告 ID: {report.id}</p>
        <p>生成时间: {new Date(report.created_at).toLocaleString()}</p>
      </div>
    </div>
  );
};

export default ReportDisplay;
